import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Clock, Mail, Menu, Phone, X } from 'lucide-react';
import { Button } from '@dreamtree-org/twreact-ui';
import { navLinks } from '@site/data';
import { useCompany, useSettings } from '@site/content';

const linkClass = ({ isActive }) =>
  `relative px-3 py-2 text-[0.95rem] font-medium transition-colors after:absolute after:inset-x-3 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-flame-x after:transition-transform ${
    isActive ? 'text-primary-700 after:scale-x-100' : 'text-ink-700 after:scale-x-0 hover:text-ink-950 hover:after:scale-x-100'
  }`;

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const company = useCompany();
  const { brand } = useSettings();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-40">
      <div className="hidden bg-ink-950 text-xs text-ink-300 md:block">
        <div className="site-container flex items-center justify-between py-2">
          <p className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent-400" aria-hidden="true" />
            {brand.topbarText}
          </p>
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-accent-400" aria-hidden="true" />
              {company.days}, {company.hours}
            </span>
            <a href={company.phoneHref} className="flex items-center gap-2 hover:text-white">
              <Phone className="h-3.5 w-3.5 text-accent-400" aria-hidden="true" />
              {company.phone}
            </a>
            <a href={`mailto:${company.email}`} className="flex items-center gap-2 hover:text-white">
              <Mail className="h-3.5 w-3.5 text-accent-400" aria-hidden="true" />
              {company.email}
            </a>
          </div>
        </div>
      </div>

      <div className={`border-b bg-white/95 backdrop-blur-md transition-shadow ${scrolled ? 'border-[var(--app-border)] shadow-sm' : 'border-transparent'}`}>
        <div className="site-container flex h-[4.5rem] items-center justify-between gap-4 sm:h-20">
          <Link to="/" className="flex min-w-0 items-center gap-3" aria-label={`${company.name} home`}>
            <img src={brand.logo} alt="" className="h-12 w-12 shrink-0 object-contain sm:h-14 sm:w-14" width="56" height="56" />
            <span className="min-w-0 leading-none">
              <span className="block truncate font-display text-xl font-extrabold tracking-wide text-ink-950 sm:text-2xl">{company.name}</span>
              <span className="mt-1 block truncate text-[0.68rem] font-medium uppercase tracking-[0.2em] text-primary-600">{brand.headerSubtitle}</span>
            </span>
          </Link>

          <nav aria-label="Primary" className="hidden items-center xl:flex">
            {navLinks.map((link) => (
              <NavLink key={link.to} to={link.to} end={link.end} className={linkClass}>
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <Button as={Link} to="/contact" className="hidden !bg-primary-600 hover:!bg-primary-700 sm:inline-flex">
              {brand.quoteLabel}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="xl:hidden"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              aria-controls="mobile-menu"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {open && (
          <nav id="mobile-menu" aria-label="Mobile" className="max-h-[calc(100vh-4.5rem)] overflow-y-auto border-t border-[var(--app-border)] bg-white xl:hidden">
            <div className="site-container flex flex-col gap-1 py-4">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 text-base font-medium ${isActive ? 'bg-primary-50 text-primary-700' : 'text-ink-700 hover:bg-ink-50'}`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button as="a" href={company.phoneHref} variant="outline" size="lg" leftIcon={<Phone className="h-4 w-4" />}>
                  Call
                </Button>
                <Button as={Link} to="/contact" size="lg" className="!bg-primary-600 hover:!bg-primary-700">
                  {brand.quoteLabel}
                </Button>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
